<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h1>📊 Owner Dashboard</h1>
      <p>Welcome back, {{ authStore.user?.email }}!</p>
      <div class="last-updated">
        <span v-if="lastUpdated">Last updated: {{ formatDateTime(lastUpdated) }}</span>
        <button @click="refreshData" :disabled="loading" class="btn btn-small btn-primary">
          <span v-if="loading" class="spinner-small"></span>
          <span v-else>🔄</span>
          Refresh
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !dashboardData" class="loading-container">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-message">
        <h3>⚠️ Error Loading Dashboard</h3>
        <p>{{ error }}</p>
        <button @click="refreshData" class="btn btn-primary">Try Again</button>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else-if="dashboardData" class="dashboard-content">
      <!-- Key Metrics Cards -->
      <div class="metrics-grid">
        <div class="metric-card revenue">
          <div class="metric-icon">💰</div>
          <div class="metric-content">
            <h3>Today's Revenue</h3>
            <p class="metric-value">${{ dashboardData.today.revenue.toFixed(2) }}</p>
            <p class="metric-subtitle">{{ dashboardData.today.transactionCount }} transactions</p>
          </div>
        </div>

        <div class="metric-card items">
          <div class="metric-icon">📦</div>
          <div class="metric-content">
            <h3>Items Sold Today</h3>
            <p class="metric-value">{{ dashboardData.today.itemsSold }}</p>
            <p class="metric-subtitle">{{ dashboardData.today.date }}</p>
          </div>
        </div>

        <div class="metric-card monthly">
          <div class="metric-icon">📈</div>
          <div class="metric-content">
            <h3>Monthly Revenue</h3>
            <p class="metric-value">${{ dashboardData.thisMonth.revenue.toFixed(2) }}</p>
            <p class="metric-subtitle">{{ dashboardData.thisMonth.transactionCount }} transactions</p>
          </div>
        </div>

        <div class="metric-card inventory">
          <div class="metric-icon">🏪</div>
          <div class="metric-content">
            <h3>Inventory Status</h3>
            <p class="metric-value">{{ dashboardData.inventory.totalProducts }}</p>
            <p class="metric-subtitle" :class="{ 'low-stock': dashboardData.inventory.lowStockProducts > 0 }">
              {{ dashboardData.inventory.lowStockProducts }} low stock
            </p>
          </div>
        </div>
      </div>

      <!-- Recent Transactions -->
      <div class="transactions-section">
        <div class="section-header">
          <h2>Recent Transactions</h2>
          <router-link to="/history" class="btn btn-secondary btn-small">View All</router-link>
        </div>

        <div v-if="dashboardData.recentTransactions.length === 0" class="empty-state">
          <div class="empty-icon">📋</div>
          <p>No recent transactions</p>
          <router-link to="/scan" class="btn btn-primary">Start Selling</router-link>
        </div>

        <div v-else class="transactions-table">
          <div class="table-header">
            <div class="col-product">Product</div>
            <div class="col-quantity">Quantity</div>
            <div class="col-price">Price</div>
            <div class="col-total">Total</div>
            <div class="col-cashier">Cashier</div>
            <div class="col-time">Time</div>
          </div>
          
          <div class="table-body">
            <div 
              v-for="transaction in dashboardData.recentTransactions.slice(0, 10)" 
              :key="transaction.id"
              class="table-row"
            >
              <div class="col-product">
                <div class="product-info">
                  <span class="product-name">{{ transaction.productName }}</span>
                  <span class="product-barcode">{{ transaction.barcode }}</span>
                </div>
              </div>
              <div class="col-quantity">{{ transaction.quantitySold }}</div>
              <div class="col-price">${{ transaction.priceAtSale.toFixed(2) }}</div>
              <div class="col-total">${{ transaction.totalAmount.toFixed(2) }}</div>
              <div class="col-cashier">{{ transaction.cashier }}</div>
              <div class="col-time">{{ formatTime(transaction.createdAt) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Weekly Sales Chart -->
      <div class="chart-section">
        <WeeklySalesChart 
          :height="400" 
          :refreshInterval="0"
          @dataLoaded="onChartDataLoaded"
          @error="onChartError"
        />
      </div>

      <!-- Business Insights -->
      <div class="insights-section">
        <h2>Business Insights</h2>
        <div class="insights-grid">
          <div class="insight-card">
            <h3>📊 Daily Performance</h3>
            <div class="insight-content">
              <div class="insight-metric">
                <span class="label">Average Transaction Value:</span>
                <span class="value">
                  ${{ dashboardData.today.transactionCount > 0 ? 
                    (dashboardData.today.revenue / dashboardData.today.transactionCount).toFixed(2) : 
                    '0.00' 
                  }}
                </span>
              </div>
              <div class="insight-metric">
                <span class="label">Items per Transaction:</span>
                <span class="value">
                  {{ dashboardData.today.transactionCount > 0 ? 
                    (dashboardData.today.itemsSold / dashboardData.today.transactionCount).toFixed(1) : 
                    '0.0' 
                  }}
                </span>
              </div>
            </div>
          </div>

          <div class="insight-card">
            <h3>📈 Monthly Progress</h3>
            <div class="insight-content">
              <div class="insight-metric">
                <span class="label">Current Month:</span>
                <span class="value">{{ formatMonth(dashboardData.thisMonth.month) }}</span>
              </div>
              <div class="insight-metric">
                <span class="label">Daily Average:</span>
                <span class="value">
                  ${{ (dashboardData.thisMonth.revenue / new Date().getDate()).toFixed(2) }}
                </span>
              </div>
            </div>
          </div>

          <div class="insight-card" :class="{ 'alert': dashboardData.inventory.lowStockProducts > 0 }">
            <h3>🚨 Stock Alerts</h3>
            <div class="insight-content">
              <div class="insight-metric">
                <span class="label">Low Stock Products:</span>
                <span class="value critical">{{ dashboardData.inventory.lowStockProducts }}</span>
              </div>
              <div class="insight-metric">
                <span class="label">Total Products:</span>
                <span class="value">{{ dashboardData.inventory.totalProducts }}</span>
              </div>
              <div v-if="dashboardData.inventory.lowStockProducts > 0" class="stock-warning">
                <router-link to="/products" class="btn btn-warning btn-small">
                  Manage Inventory
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <h2>Quick Actions</h2>
        <div class="actions-grid">
          <router-link to="/scan" class="action-card">
            <div class="action-icon">📱</div>
            <div class="action-content">
              <h3>Start Selling</h3>
              <p>Open barcode scanner</p>
            </div>
          </router-link>

          <router-link to="/products/new" class="action-card">
            <div class="action-icon">➕</div>
            <div class="action-content">
              <h3>Add Product</h3>
              <p>Create new product</p>
            </div>
          </router-link>

          <router-link to="/products" class="action-card">
            <div class="action-icon">📦</div>
            <div class="action-content">
              <h3>Manage Products</h3>
              <p>Edit inventory</p>
            </div>
          </router-link>

          <router-link to="/history" class="action-card">
            <div class="action-icon">📋</div>
            <div class="action-content">
              <h3>Sales History</h3>
              <p>View all transactions</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'
import WeeklySalesChart from '../components/WeeklySalesChart.vue'

const authStore = useAuthStore()

// State
const dashboardData = ref(null)
const loading = ref(false)
const error = ref(null)
const lastUpdated = ref(null)

// Computed properties
// const user = computed(() => authStore.user) // Removed unused variable

// Methods
const fetchDashboardData = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await api.get('/dashboard/summary')
    dashboardData.value = response.data.data
    lastUpdated.value = new Date()
  } catch (err) {
    console.error('Dashboard fetch error:', err)
    error.value = err.response?.data?.error || 'Failed to load dashboard data'
  } finally {
    loading.value = false
  }
}

const refreshData = () => {
  fetchDashboardData()
}

// Chart event handlers
const onChartDataLoaded = (chartData) => {
  console.log('Chart data loaded:', chartData)
}

const onChartError = (error) => {
  console.error('Chart error:', error)
}

// Utility functions
const formatDateTime = (date) => {
  return new Date(date).toLocaleString()
}

const formatTime = (date) => {
  return new Date(date).toLocaleTimeString()
}

const formatMonth = (monthString) => {
  const [year, month] = monthString.split('-')
  const date = new Date(year, month - 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

// Lifecycle
onMounted(() => {
  fetchDashboardData()
})
</script>

<style scoped>
.dashboard {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

.dashboard-header h1 {
  color: #2c3e50;
  margin: 0;
}

.dashboard-header p {
  color: #7f8c8d;
  margin: 5px 0 0 0;
}

.last-updated {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 0.9em;
  color: #6c757d;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: 40px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  color: #721c24;
}

.error-message h3 {
  margin: 0 0 10px 0;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.metric-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 20px;
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.metric-card.revenue {
  border-left: 4px solid #28a745;
}

.metric-card.items {
  border-left: 4px solid #007bff;
}

.metric-card.monthly {
  border-left: 4px solid #6f42c1;
}

.metric-card.inventory {
  border-left: 4px solid #fd7e14;
}

.metric-icon {
  font-size: 2.5em;
  opacity: 0.8;
}

.metric-content {
  flex: 1;
}

.metric-content h3 {
  color: #495057;
  margin: 0 0 8px 0;
  font-size: 1em;
  font-weight: 600;
}

.metric-value {
  font-size: 2.2em;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  line-height: 1.1;
}

.metric-subtitle {
  color: #6c757d;
  font-size: 0.9em;
  margin: 4px 0 0 0;
}

.metric-subtitle.low-stock {
  color: #dc3545;
  font-weight: 600;
}

/* Transactions Section */
.transactions-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 40px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.section-header h2 {
  color: #2c3e50;
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.empty-icon {
  font-size: 4em;
  margin-bottom: 20px;
}

.transactions-table {
  width: 100%;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
}

.table-body {
  display: flex;
  flex-direction: column;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #f8f9fa;
  align-items: center;
}

.table-row:hover {
  background: #f8f9fa;
}

.product-info {
  display: flex;
  flex-direction: column;
}

.product-name {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.product-barcode {
  font-size: 0.8em;
  color: #6c757d;
  font-family: monospace;
}

/* Chart Section */
.chart-section {
  margin-bottom: 40px;
}

/* Insights Section */
.insights-section {
  margin-bottom: 40px;
}

.insights-section h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.insight-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.insight-card.alert {
  border-left: 4px solid #dc3545;
  background: #fff5f5;
}

.insight-card h3 {
  color: #2c3e50;
  margin: 0 0 15px 0;
}

.insight-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.insight-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.insight-metric .label {
  color: #6c757d;
  font-size: 0.9em;
}

.insight-metric .value {
  font-weight: 600;
  color: #2c3e50;
}

.insight-metric .value.critical {
  color: #dc3545;
  font-weight: 700;
}

.stock-warning {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
}

/* Quick Actions */
.quick-actions h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.action-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 20px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  color: inherit;
}

.action-icon {
  font-size: 2.5em;
  opacity: 0.8;
}

.action-content h3 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 1.1em;
}

.action-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9em;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
}

.btn-small {
  padding: 6px 12px;
  font-size: 0.8em;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .insights-grid {
    grid-template-columns: 1fr;
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .table-header {
    display: none;
  }
  
  .table-row {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 10px;
    border: 1px solid #e9ecef;
  }
  
  .table-row > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .table-row > div:before {
    content: attr(data-label);
    font-weight: 600;
    color: #495057;
  }
}
</style> 